import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Repeat, Check } from 'lucide-react';
import { toast } from 'sonner';
import { convertToSeries, clearSeries } from '../../lib/api';

/**
 * RecurrenceEditor — modal per impostare/modificare la regola di ricorrenza
 * di una MIT o di un'Azione.
 *
 * Props:
 *   - parent: { id, kind: 'mit'|'azione', testo, recurrence_rule, recurrence_end }
 *   - onClose()  - chiamato sempre alla chiusura
 *   - onSaved()  - chiamato dopo un save/clear riuscito
 *
 * Pattern supportati:
 *   - DAILY (interval N)
 *   - WEEKLY (interval N, opz. giorni della settimana selezionati)
 *   - MONTHLY (interval N, opz. giorno specifico del mese)
 *   - Termine: senza fine | data specifica
 */
const WEEKDAYS = [
  { code: 'MO', label: 'Lun' },
  { code: 'TU', label: 'Mar' },
  { code: 'WE', label: 'Mer' },
  { code: 'TH', label: 'Gio' },
  { code: 'FR', label: 'Ven' },
  { code: 'SA', label: 'Sab' },
  { code: 'SU', label: 'Dom' },
];

export default function RecurrenceEditor({ parent, onClose, onSaved }) {
  const existing = parent?.recurrence_rule || null;
  const [freq, setFreq] = useState(existing?.freq || 'DAILY');
  const [interval, setIntervalVal] = useState(existing?.interval || 1);
  const [byweekday, setByweekday] = useState(existing?.byweekday || []);
  const [bymonthday, setBymonthday] = useState(existing?.bymonthday || '');
  const [endDate, setEndDate] = useState(parent?.recurrence_end || '');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const toggleWeekday = (code) => {
    setByweekday((wd) => wd.includes(code) ? wd.filter((x) => x !== code) : [...wd, code]);
  };

  const handleSave = async () => {
    if (submitting) return;
    const rule = { freq, interval: Math.max(1, parseInt(interval, 10) || 1) };
    if (freq === 'WEEKLY' && byweekday.length > 0) rule.byweekday = byweekday;
    if (freq === 'MONTHLY' && bymonthday) rule.bymonthday = Math.max(1, Math.min(31, parseInt(bymonthday, 10) || 1));
    setSubmitting(true);
    try {
      await convertToSeries(parent.kind, parent.id, rule, endDate || null);
      toast.success('Ricorrenza salvata');
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Salvataggio fallito');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClear = async () => {
    if (!existing) { onClose(); return; }
    if (!window.confirm('Rimuovere la ricorrenza? La riga resta come elemento singolo. Gli override saranno persi.')) return;
    setSubmitting(true);
    try {
      await clearSeries(parent.kind, parent.id);
      toast.success('Ricorrenza rimossa');
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rimozione fallita');
    } finally {
      setSubmitting(false);
    }
  };

  const isWeekly = freq === 'WEEKLY';
  const isMonthly = freq === 'MONTHLY';

  // Anteprima testo regola
  const previewText = (() => {
    if (freq === 'DAILY') return interval === 1 ? 'Ogni giorno' : `Ogni ${interval} giorni`;
    if (isWeekly) {
      const days = byweekday.length > 0 ? ` (${byweekday.map((c) => WEEKDAYS.find((w) => w.code === c)?.label).filter(Boolean).join(', ')})` : '';
      return `${interval === 1 ? 'Ogni settimana' : `Ogni ${interval} settimane`}${days}`;
    }
    if (isMonthly) {
      const md = bymonthday ? ` (giorno ${bymonthday})` : '';
      return `${interval === 1 ? 'Ogni mese' : `Ogni ${interval} mesi`}${md}`;
    }
    return '';
  })();

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh] px-4 bg-black/30 backdrop-blur-sm"
      onClick={onClose}
      data-testid="recurrence-editor-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-canvas border border-ink shadow-2xl"
        data-testid="recurrence-editor-modal"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-line">
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-soft inline-flex items-center gap-2">
            <Repeat size={12} strokeWidth={1.5} />
            Ricorrenza · {parent?.kind === 'mit' ? 'MIT' : 'Azione'}
          </span>
          <button onClick={onClose} className="text-mute hover:text-ink" aria-label="Chiudi" data-testid="recurrence-editor-close">
            <X size={16} strokeWidth={1.5} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="font-body text-sm text-soft truncate" title={parent?.testo}>
            {parent?.testo}
          </div>

          {/* Frequenza */}
          <div className="flex gap-2">
            {['DAILY', 'WEEKLY', 'MONTHLY'].map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFreq(f)}
                className={'flex-1 font-mono text-[10px] uppercase tracking-[0.18em] px-2 py-2 border transition-colors ' +
                  (freq === f ? 'border-ink bg-ink text-canvas' : 'border-line text-soft hover:border-ink')}
                data-testid={`recurrence-freq-${f}`}
              >
                {f === 'DAILY' ? 'Giorno' : f === 'WEEKLY' ? 'Settimana' : 'Mese'}
              </button>
            ))}
          </div>

          {/* Intervallo */}
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-soft block mb-1">
              Intervallo · ogni N {freq === 'DAILY' ? 'giorni' : isWeekly ? 'settimane' : 'mesi'}
            </span>
            <input
              type="number"
              min={1}
              max={365}
              value={interval}
              onChange={(e) => setIntervalVal(e.target.value)}
              className="w-24 px-3 py-2 border border-line focus:border-ink focus:outline-none font-mono text-sm"
              data-testid="recurrence-interval"
            />
          </label>

          {/* Giorni settimana (solo WEEKLY) */}
          {isWeekly && (
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-soft mb-1.5">
                Giorni della settimana (opzionale, vuoto = stesso giorno del parent)
              </div>
              <div className="flex flex-wrap gap-1.5">
                {WEEKDAYS.map((w) => (
                  <button
                    key={w.code}
                    type="button"
                    onClick={() => toggleWeekday(w.code)}
                    className={'font-mono text-[11px] uppercase tracking-[0.15em] px-2 py-1.5 border transition-colors ' +
                      (byweekday.includes(w.code) ? 'border-ink bg-ink text-canvas' : 'border-line text-soft hover:border-ink')}
                    data-testid={`recurrence-weekday-${w.code}`}
                  >
                    {w.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Giorno del mese (solo MONTHLY) */}
          {isMonthly && (
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-soft block mb-1">
                Giorno del mese (opzionale)
              </span>
              <input
                type="number"
                min={1}
                max={31}
                placeholder="Vuoto = stesso giorno del parent"
                value={bymonthday}
                onChange={(e) => setBymonthday(e.target.value)}
                className="w-32 px-3 py-2 border border-line focus:border-ink focus:outline-none font-mono text-sm"
                data-testid="recurrence-bymonthday"
              />
            </label>
          )}

          {/* Data fine (opzionale) */}
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-soft block mb-1">
              Termina entro (opzionale)
            </span>
            <input
              type="date"
              value={endDate || ''}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-48 px-3 py-2 border border-line focus:border-ink focus:outline-none font-mono text-sm"
              data-testid="recurrence-end"
            />
          </label>

          {/* Anteprima */}
          <div className="mt-2 px-3 py-2 bg-tint border border-line font-body text-sm">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-mute mr-2">Anteprima:</span>
            {previewText}
            {endDate && <span className="text-mute"> · fino al {endDate}</span>}
          </div>
        </div>

        <div className="px-5 py-3 border-t border-line flex items-center justify-between bg-tint">
          {existing ? (
            <button
              type="button"
              onClick={handleClear}
              disabled={submitting}
              className="font-mono text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 text-accent hover:text-risk border border-transparent hover:border-accent transition-colors disabled:opacity-50"
              data-testid="recurrence-editor-clear"
            >
              Rimuovi ricorrenza
            </button>
          ) : <span />}
          <button
            type="button"
            onClick={handleSave}
            disabled={submitting}
            className="font-mono text-xs uppercase tracking-[0.2em] px-5 py-2 border border-ink bg-ink text-canvas hover:opacity-90 disabled:opacity-50 transition-opacity inline-flex items-center gap-2"
            data-testid="recurrence-editor-save"
          >
            <Check size={13} strokeWidth={1.5} />
            {submitting ? 'Salvo…' : (existing ? 'Aggiorna' : 'Crea serie')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
